import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  viewAdmin,
  ViewAdminApiResponse,
  ViewAdminUser,
} from '../api/viewAdminApiService';
import { useUserPrivileges } from '@/shared/hooks/useUserPrivileges';

// ============================================================================
// Constants
// ============================================================================

export const PROFILE_NAV_ITEMS = [
  { id: 'PodstawoweInformacje', label: 'Basic Information' },
  { id: 'Uprawnienia', label: 'Uprawnienia' },
] as const;

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generates initials from a user's name
 * Takes first letter of each word, up to 2 characters
 */
export const getUserInitials = (name: string | null | undefined): string => {
  if (!name) {
    return '';
  }
  const names = name.trim().split(' ').filter(Boolean);
  if (names.length >= 2) {
    const first = names[0];
    const last = names[names.length - 1];
    if (first?.[0] && last?.[0]) {
      return `${first[0]}${last[0]}`.toUpperCase();
    }
  }
  return name.substring(0, 2).toUpperCase();
};

import {
  getStatusLabel as getSharedStatusLabel,
  getStatusBadgeClasses as getSharedStatusBadgeClasses,
} from '@/shared/utils/statusBadge';

/**
 * Gets the status label in Polish
 * Re-exported from shared utils for convenience
 */
export const getStatusLabel = (
  status: ViewAdminUser['status'] | null | undefined
): string => {
  return getSharedStatusLabel(status);
};

/**
 * Gets status badge classes based on status
 * Re-exported from shared utils for convenience
 */
export const getStatusBadgeClasses = (
  status: ViewAdminUser['status'] | null | undefined
): { container: string; dot: string } => {
  return getSharedStatusBadgeClasses(status);
};

// ============================================================================
// Hook Return Type
// ============================================================================

export interface UseViewAdminReturn {
  // Data state
  userData: ViewAdminApiResponse | undefined;
  user: ViewAdminUser | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  privilegeError: string | null;

  // Computed values
  initials: string;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };

  // Actions
  refetch: () => Promise<void>;
}

// ============================================================================
// useViewAdmin Hook
// ============================================================================

/**
 * Custom hook for managing ViewAdmin page state and logic
 * Handles fetching user data, error states, and computed values
 */
export const useViewAdmin = (userId: string): UseViewAdminReturn => {
  // State
  const [userData, setUserData] = useState<ViewAdminApiResponse | undefined>(
    undefined
  );
  const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false);
  const [privilegeError, setPrivilegeError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Fetch caller's privileges to check if they have permission to view admins
  const { privileges, isLoading: privLoading } = useUserPrivileges();

  // Fetch user data function
  const fetchUserData = useCallback(async () => {
    // Don't fetch until privileges have loaded
    if (privLoading) {
      return;
    }

    // Check if caller has permission to view admin users
    if (!privileges.adminUserView) {
      setPrivilegeError(
        'Nie masz uprawnień do wyświetlenia tego administratora'
      );
      setIsLoading(false);
      return;
    }

    setPrivilegeError(null);
    setIsLoading(true);
    try {
      const response = await viewAdmin({
        userId,
        skipGlobalErrorRedirect: true,
      });

      if (response.success && response.data) {
        setUserData(response.data);
        setShowErrorScreen(false);
      } else if (response.status === 403) {
        setPrivilegeError(
          'Nie masz uprawnień do wyświetlenia tego administratora'
        );
      } else {
        setShowErrorScreen(true);
        console.error('Error fetching user data:', response.error);
      }
    } catch (error) {
      setShowErrorScreen(true);
      console.error('Error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, privLoading, privileges]);

  // Fetch user data on mount and when userId/privileges change
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Computed values
  const user = useMemo(() => userData?.user, [userData]);

  const initials = useMemo(() => getUserInitials(user?.name), [user?.name]);

  const statusLabel = useMemo(
    () => getStatusLabel(user?.status),
    [user?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getStatusBadgeClasses(user?.status),
    [user?.status]
  );

  return {
    // Data state
    userData,
    user,
    isLoading,
    showErrorScreen,
    privilegeError,

    // Computed values
    initials,
    statusLabel,
    statusBadgeClasses,

    // Actions
    refetch: fetchUserData,
  };
};
